import { useMemo } from "react";
import { Link } from "react-router-dom";
import { usePageMeta, useJsonLd, breadcrumbJsonLd } from "../lib/seo";

export function AboutPage() {
  usePageMeta({
    title: "About",
    description:
      "MarketOnBase is a marketplace for digital goods where payment is held in a non-custodial smart contract on Base until the buyer has what they paid for. Learn who it's for and how it protects both sides.",
    path: "/about",
  });

  useJsonLd(
    useMemo(
      () =>
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "About", path: "/about" },
        ]),
      [],
    ),
  );

  return (
    <article className="stack" style={{ maxWidth: 720 }}>
      <div>
        <h1>About MarketOnBase</h1>
        <p className="muted">
          A marketplace for digital goods where neither side has to trust the other first.
        </p>
      </div>

      <section className="stack">
        <h2>The problem we set out to solve</h2>
        <p>
          Selling a digital item to a stranger is an awkward standoff. If the buyer pays first,
          nothing forces the seller to deliver. If the seller sends the file first, nothing forces
          the buyer to pay — and once a file has been downloaded, it can&apos;t be un-downloaded.
          The usual answer is a middleman who holds the money and, in doing so, becomes something
          both sides have to trust with their funds.
        </p>
        <p>
          MarketOnBase removes that trust requirement. When a buyer pays, the USDC goes into an{" "}
          <strong>smart contract on Base</strong> — not into a company bank account, and not
          into a wallet we control. The contract holds the principal until the buyer releases it,
          the dispute window closes, or an arbitrator resolves a dispute. We cannot take those
          funds, redirect them, or freeze them, because the contract simply has no function that
          would let us.
        </p>
      </section>

      <section className="stack">
        <h2>What that means in practice</h2>
        <ul>
          <li>
            <strong>Your money is yours until you say otherwise.</strong> Held principal can
            only move to the buyer or the seller, along paths written into the contract before
            either of you showed up.
          </li>
          <li>
            <strong>You keep your own keys.</strong> Signing in creates a Coinbase-managed Smart
            Account. It&apos;s a real, general-purpose Base account that belongs to you — not a
            balance in our database.
          </li>
          <li>
            <strong>Every file is scanned before it can be sold.</strong> Uploads land in a private
            quarantine bucket and are scanned for malware; only clean files can be published.
          </li>
          <li>
            <strong>Buyers get a dispute window.</strong> If what arrives isn&apos;t what was
            promised, there&apos;s a defined window to open a dispute before funds can be claimed
            by the seller.
          </li>
        </ul>
        <p>
          The <Link to="/how-it-works">how it works</Link> page walks through the full flow step by
          step, including exactly when money moves.
        </p>
      </section>

      <section className="stack">
        <h2>What we charge</h2>
        <p>
          A flat <strong>10% platform fee</strong> on top of the seller&apos;s listed price, paid
          by the buyer and itemized before anything is signed. The fee is charged when the funding
          transaction confirms onchain and is <strong>not refundable</strong>, including when a
          dispute later returns the principal to the buyer. Only the principal — the seller&apos;s
          listed price — is ever released, refunded, or split.
        </p>
        <p>
          The fee rate is fixed in the contract at deployment. There is no setter function, so it
          cannot be raised on an order that&apos;s already funded.
        </p>
      </section>

      <section className="stack">
        <h2>What we are honest about</h2>
        <p>
          Cryptocurrency transactions are irreversible. There is no chargeback, and no support
          ticket that can claw back a completed onchain settlement. That is a real tradeoff for the
          trust guarantees above, and we&apos;d rather say so plainly than bury it.
        </p>
        <p>
          This build has not completed an independent third-party smart contract audit or a
          production legal review. Read the <Link to="/terms">terms of service</Link> and{" "}
          <Link to="/privacy">privacy policy</Link> before using it, and don&apos;t transact with
          amounts you aren&apos;t prepared to lose.
        </p>
      </section>

      <section className="stack">
        <h2>Questions</h2>
        <p>
          The <Link to="/faq">FAQ</Link> covers fees, disputes, refunds, wallets, and what happens
          when something goes wrong.
        </p>
      </section>
    </article>
  );
}
